import {
  CloseOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import { Button, Calendar, Dropdown, Segmented } from 'antd';
import dayjs from 'dayjs';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import { ProjectSearchDateOperator } from '../tokens';
import { formatProjectSearchDateFilterLabel } from './dateFilter';
import styles from './styles.module.css';

type Props = {
  disableFutureDates?: boolean;
  isContainerOpen: boolean;
  onChange: (value: string, operator: ProjectSearchDateOperator) => void;
  operator: ProjectSearchDateOperator;
  sentencePrefix: string;
  triggerLabel: string;
  value: string;
};

const DATE_FILTER_OPERATORS: {
  label: string;
  value: ProjectSearchDateOperator;
}[] = [
  { label: 'Before', value: 'before' },
  { label: 'On', value: 'on' },
  { label: 'After', value: 'after' }
];

const DateFilter: React.FC<Props> = ({
  disableFutureDates = false,
  isContainerOpen,
  onChange,
  operator,
  sentencePrefix,
  triggerLabel,
  value
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [pendingOperator, setPendingOperator] = React.useState<
    ProjectSearchDateOperator
  >(operator);
  const [calendarValue, setCalendarValue] = React.useState(() =>
    value ? dayjs(value) : dayjs()
  );

  React.useEffect(() => {
    if (!isContainerOpen) setIsOpen(false);
  }, [isContainerOpen]);

  const overlay = (
    <div className={styles.dropdownPanel}>
      <Segmented
        block
        className={styles.operator}
        onChange={nextOperator =>
          setPendingOperator(nextOperator as ProjectSearchDateOperator)
        }
        options={DATE_FILTER_OPERATORS}
        size="small"
        value={pendingOperator}
      />
      <Calendar
        className={styles.calendar}
        disabledDate={
          disableFutureDates ? date => date.isAfter(dayjs(), 'day') : undefined
        }
        fullscreen={false}
        headerRender={({ value: visibleMonth }) => (
          <div className={styles.calendarHeader}>
            <Button
              aria-label="Previous month"
              icon={<LeftOutlined />}
              onClick={() =>
                setCalendarValue(visibleMonth.subtract(1, 'month'))
              }
              size="small"
              type="text"
            />
            <span>{visibleMonth.format('MMM YYYY')}</span>
            <Button
              aria-label="Next month"
              icon={<RightOutlined />}
              onClick={() => setCalendarValue(visibleMonth.add(1, 'month'))}
              size="small"
              type="text"
            />
          </div>
        )}
        onSelect={(date, info) => {
          if (info.source !== 'date') return;

          setCalendarValue(date);
          onChange(date.format('YYYY-MM-DD'), pendingOperator);
          setIsOpen(false);
        }}
        value={calendarValue}
      />
    </div>
  );

  return (
    <Dropdown
      getPopupContainer={trigger => trigger.parentElement || document.body}
      onOpenChange={open => {
        setIsOpen(open);
        if (open) {
          setPendingOperator(operator);
          setCalendarValue(value ? dayjs(value) : dayjs());
        }
      }}
      open={isOpen}
      overlay={overlay}
      overlayClassName={styles.dropdownOverlay}
      placement="bottomLeft"
      trigger={['click']}
    >
      {value ? (
        <span className={styles.selectedDateTrigger}>
          <Button
            aria-label={`Edit ${triggerLabel.toLowerCase()} filter`}
            className={styles.selectedDateLabel}
            shape="round"
            size="small"
            type="primary"
          >
            {formatProjectSearchDateFilterLabel({
              emptyLabel: triggerLabel,
              operator,
              sentencePrefix,
              value
            })}
          </Button>
          <Button
            aria-label={`Clear ${triggerLabel.toLowerCase()} filter`}
            className={styles.selectedDateClear}
            icon={<CloseOutlined />}
            onClick={event => {
              event.stopPropagation();
              setIsOpen(false);
              onChange('', 'on');
            }}
            shape="circle"
            size="small"
            type="primary"
          />
        </span>
      ) : (
        <Button shape="round" size="small">
          {triggerLabel}
          <DownOutlined />
        </Button>
      )}
    </Dropdown>
  );
};

export default DateFilter;
